import React from "react"

import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Roles_Enum,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    UserCreateFragment, 
    UserCreateSchema, 
    UserUpdateFragment, 
    UserUpdateSchema 
} from "src/components/agency/operations/administrator/users/AgencyOperationsAdminUserSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = UserCreateFragment

let initialState: FormPayloadType = {
    role: User_Roles_Enum.Administrator,
    type: User_Type_Enum.Operations,
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateUserDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    const { tenant_id } = useAppState().auth;

    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({ tenant_id, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={UserCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add User'
            action={CreateUserDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: UserUpdateFragment = {
    status: User_Status_Enum.Active,
    email: "",
    role: User_Roles_Enum.Administrator,
    type: User_Type_Enum.Operations,
    first_name: "",
    last_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    color: "",
    timezone: 'America/New_York'
};

export const UpdateUserDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;



    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: UserUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<UserUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<UserUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={UserUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateUserAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateUserDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateUserDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}